import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
} from '@ads/components-react';
import { Link, useBlocker } from '@tanstack/react-router';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { SaveClassModuleViewModel } from '@/shared/api/generated';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

import { useClassModuleForm } from '../../../context/ClassModuleFormContext';
import { buildSaveViewModel } from '../../../utils/buildSaveViewModel';
import { DeleteConfirmModal } from '../../molecules/DeleteConfirmModal';
import { UnsavedChangesModal } from '../../molecules/UnsavedChangesModal';
import { DisciplineFormModal } from '../../organisms/DisciplineFormModal';
import { DisciplinesSection } from '../../organisms/DisciplinesSection/DisciplinesSection';
import { GeneralSection } from '../../organisms/GeneralSection/GeneralSection';
import { LinkedClassesSection } from '../../organisms/LinkedClassesSection/LinkedClassesSection';

export interface ClassModuleFormTemplateProps {
  readonly title: string;
  readonly isSubmitting: boolean;
  /** Persiste o módulo. Deve resolver no sucesso e rejeitar no erro. */
  readonly onSubmit: (payload: SaveClassModuleViewModel) => Promise<void>;
  /** Chamado após um save bem-sucedido (com o form já resetado): feedback + navegação. */
  readonly onSaved: () => void;
  readonly onCancel: () => void;
}

const CARD =
  'rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs shadow-level1';

/**
 * Layout da tela de cadastro/edição de módulo, em página única (seções Geral e
 * Disciplinas empilhadas em cards, como no protótipo). Orquestra o submit
 * convertendo o formulário no payload de escrita. O cabeçalho da página vem do
 * `PageHeader`; a feature não estiliza o header da aplicação.
 */
export function ClassModuleFormTemplate({
  title,
  isSubmitting,
  onSubmit,
  onSaved,
  onCancel,
}: ClassModuleFormTemplateProps) {
  const { t } = useTranslation('classModuleManagement');
  const { form, pendingDelete, confirmDelete, cancelDelete } = useClassModuleForm();

  // Bloqueia a saída (Cancelar, links, voltar do navegador) com alterações não salvas.
  // `formState` do RHF é um Proxy: `isDirty` só é calculado e mantido atualizado
  // quando lido durante o render (isso habilita a subscription). Se fosse lido só
  // dentro do `shouldBlockFn`, ficaria travado em `false` e o modal nunca abriria.
  // Espelhamos o valor num ref para o `shouldBlockFn` (executado fora do render)
  // enxergar o estado ao vivo no momento da navegação.
  const { isDirty } = form.formState;
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const blocker = useBlocker({
    shouldBlockFn: () => isDirtyRef.current,
    withResolver: true,
  });

  const handleSave = form.handleSubmit(async values => {
    try {
      await onSubmit(buildSaveViewModel(values));
      form.reset(values);
      // `form.reset` só reflete no `isDirty` no próximo render, mas a navegação do
      // pós-save (quando há) acontece ainda neste tick: espelha o estado limpo já
      // para o `shouldBlockFn` não bloquear a saída intencional.
      isDirtyRef.current = false;
      onSaved();
    } catch {
      // Erro já sinalizado pela página (alert em onError); mantém o form sujo.
    }
  });

  return (
    <div className="flex flex-col gap-xxs">
      <PageHeader
        title={title}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to="/class-modules">{t('breadcrumb.list')}</Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{title}</BreadcrumbPage>
          </Breadcrumb>
        }
      />

      <section className={`${CARD} flex flex-col gap-xs`}>
        <GeneralSection />
        <LinkedClassesSection />
      </section>

      <section className={CARD}>
        <DisciplinesSection />
      </section>

      <div className="flex gap-nano">
        <Button
          hierarchy="primary"
          size="md"
          isLoading={isSubmitting}
          onClick={() => void handleSave()}
        >
          {t('actions.save')}
        </Button>
        <Button hierarchy="secondary" size="md" onClick={onCancel}>
          {t('actions.cancel')}
        </Button>
      </div>

      <DisciplineFormModal />

      <DeleteConfirmModal
        open={pendingDelete !== null}
        itemName={pendingDelete?.name ?? ''}
        onOpenChange={open => !open && cancelDelete()}
        onConfirm={confirmDelete}
      />

      <UnsavedChangesModal
        open={blocker.status === 'blocked'}
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </div>
  );
}
